import {getPriorityInfo, getStatusInfo} from '../types';
import {Star, CalendarDays, Flag, Clock, FileText, LucideIcon, BookAIcon} from 'lucide-react';
import React from 'react';
import {cn} from '@/lib/tailwind-utils';
import {Separator} from '@/shadcn_components/ui/separator';

const TaskOverview = ({task}) => {
  return (
    <div className="flex flex-row flex-wrap gap-4">
      <TaskDetail record={task} />
      <TaskStatusInfo record={task} />
    </div>
  );
};

interface InfoViewProps {
  icon?: LucideIcon;
  label: string;
  label_className?: string;
  value: React.ReactNode;
  value_className?: string;
}

const InfoView: React.FC<InfoViewProps> = ({
  icon: Icon,
  label,
  label_className,
  value,
  value_className,
}) => {
  return (
    <div className="flex flex-row mb-2">
      {Icon && <Icon className="h-4 w-4 mx-1 shrink-0 text-primary self-center" />}
      <span className={cn('font-medium text-lg whitespace-nowrap', label_className)}>{label}</span>
      <span className={cn('ml-1 text-lg', value_className)}>{value}</span>
    </div>
  );
};

const TaskStatusInfo = ({record}) => {
  const formatDate = dateString => {
    if (!dateString) return 'Chưa thiết lập';
    return new Date(dateString).toLocaleDateString('vi-VN');
  };

  const priorityInfo = getPriorityInfo(record?.priority);
  const statusInfo = getStatusInfo(record?.status);

  const calculateDaysDiff = (date1, date2) => {
    if (!date1 || !date2) return null;
    const d1 = new Date(date1).getTime();
    const d2 = new Date(date2).getTime();
    const diffTime = d2 - d1;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const initialVariance = calculateDaysDiff(record?.initial_end_date, record?.actual_end_date);
  const rescheduledVariance = calculateDaysDiff(
    record?.rescheduled_end_date,
    record?.actual_end_date,
  );

  return (
    <div className="bg-white rounded-lg border p-4 w-full lg:max-w-[calc(50%-20px)]">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Trạng thái công việc</h2>
      </div>

      <InfoView
        icon={Star}
        label="Trạng thái:"
        value={statusInfo.text}
        value_className={statusInfo.color}
      />

      <InfoView icon={CalendarDays} label="Ngày bắt đầu:" value={formatDate(record?.start_date)} />

      <InfoView
        icon={CalendarDays}
        label="Ngày kết thúc dự kiến:"
        value={formatDate(record?.initial_end_date)}
      />

      {record?.rescheduled_end_date && (
        <InfoView
          icon={CalendarDays}
          label="Ngày kết thúc điều chỉnh:"
          value={formatDate(record?.rescheduled_end_date)}
        />
      )}

      {record?.actual_end_date && (
        <InfoView
          icon={CalendarDays}
          label="Ngày hoàn thành thực tế:"
          value={formatDate(record?.actual_end_date)}
        />
      )}

      <InfoView
        icon={Clock}
        label="Số ngày làm dự kiến:"
        value={`${record?.expected_work_days || 0} ngày`}
      />

      {initialVariance !== null && (
        <InfoView
          icon={Clock}
          label="Chênh lệch kế hoạch ban đầu:"
          value={`${initialVariance > 0 ? '+' : ''}${initialVariance} ngày`}
          value_className={initialVariance > 0 ? 'text-red-500' : 'text-green-500'}
        />
      )}

      {rescheduledVariance !== null && (
        <InfoView
          icon={Clock}
          label="Chênh lệch kế hoạch điều chỉnh:"
          value={`${rescheduledVariance > 0 ? '+' : ''}${rescheduledVariance} ngày`}
          value_className={rescheduledVariance > 0 ? 'text-red-500' : 'text-green-500'}
        />
      )}

      <InfoView
        icon={Flag}
        label="Độ ưu tiên:"
        value={priorityInfo.text}
        value_className={priorityInfo.color}
      />

      {record?.code && <InfoView icon={FileText} label="Mã công việc:" value={record?.code} />}

      {record?.project_id && (
        <InfoView icon={FileText} label="Dự án:" value={`ID: ${record?.project_id}`} />
      )}

      {record?.parent_id && (
        <InfoView icon={FileText} label="Công việc cha:" value={`ID: ${record?.parent_id}`} />
      )}
    </div>
  );
};

const TaskDetail = ({record}) => {
  const formatDateTime = dateTimeString => {
    if (!dateTimeString) return 'Chưa thiết lập';
    const date = new Date(dateTimeString);
    return date.toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div className="bg-white rounded-lg border p-4 w-full lg:max-w-[50%]">
      <div className="flex justify-between items-center mb-2">
        <div className="w-full p-6 rounded-lg bg-primary">
          <h1 className="text-2xl font-semibold text-white">{record.name}</h1>
        </div>
      </div>
      <Separator />
      <h3 className="text-lg font-semibold m-2">Mô tả công việc</h3>
      <div className="m-2">
        {record.code && <InfoView icon={Star} label="Mã Công việc:" value={record.code} />}

        <InfoView icon={Clock} label="Ngày tạo:" value={formatDateTime(record.created_at)} />

        {record.project?.name && (
          <InfoView icon={FileText} label="Dự án:" value={record.project.name} />
        )}

        {record.parent?.name && (
          <InfoView icon={FileText} label="Công việc cha:" value={record.parent.name} />
        )}

        <InfoView
          icon={BookAIcon}
          label="Mô tả:"
          value={record.description ? record.description : 'Không có mô tả'}
        />
      </div>
    </div>
  );
};

export default TaskOverview;
